'use client';

import { StyledCheckbox, StyledInput } from '@ui/Form';
import Dropdown from '@ui/Dropdown';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableField } from '~/components/Formular/Fields/TableField';
import { ImportField, optionLabel } from './types';

const ADDRESS_PARTS = ['street', 'streetnumber', 'postalcode', 'city'] as const;

interface Props {
    /** Null for the fixed name and email columns. */
    field: ImportField | null;
    columnKey: string;
    value: any;
    isInvalid?: boolean;
    isDisabled?: boolean;
    onChange: (value: any) => void;
}

/**
 * Text keeps a local draft and saves on blur, so the list poll cannot overwrite what is being typed
 * and not every keystroke becomes a request. Choices save immediately.
 */
const TextCell = ({
    value,
    type = 'text',
    isInvalid,
    isDisabled,
    placeholder,
    onChange
}: {
    value: string;
    type?: string;
    isInvalid?: boolean;
    isDisabled?: boolean;
    placeholder?: string;
    onChange: (value: string | null) => void;
}) => {
    const [draft, setDraft] = useState(value ?? '');
    const [isFocused, setIsFocused] = useState(false);
    const [syncedValue, setSyncedValue] = useState(value);

    // Follows the server value while nobody is typing; what is being typed wins until blur.
    if (!isFocused && value !== syncedValue) {
        setSyncedValue(value);
        setDraft(value ?? '');
    }

    return (
        <StyledInput
            flush
            fullWidth
            size="sm"
            type={type}
            value={draft}
            placeholder={placeholder}
            isInvalid={isInvalid}
            disabled={isDisabled}
            onFocus={() => setIsFocused(true)}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
                setIsFocused(false);
                if (draft !== (value ?? '')) onChange('' === draft ? null : draft);
            }}
        />
    );
};

const CellEditor = ({ field, columnKey, value, isInvalid, isDisabled, onChange }: Props) => {
    const { t } = useTranslation('bookings');

    if (!field) {
        return (
            <TextCell
                value={value ?? ''}
                type={'email' === columnKey ? 'email' : 'text'}
                isInvalid={isInvalid}
                isDisabled={isDisabled}
                onChange={onChange}
            />
        );
    }

    const options = field.options ?? [];

    switch (field.type) {
        case 'number':
            return (
                <TextCell
                    type="number"
                    value={null === value || undefined === value ? '' : String(value)}
                    isInvalid={isInvalid}
                    isDisabled={isDisabled}
                    onChange={(next) => onChange(null === next ? null : Number(next))}
                />
            );

        case 'date':
            return (
                <TextCell
                    type="date"
                    value={typeof value === 'string' ? value.slice(0, 10) : ''}
                    isInvalid={isInvalid}
                    isDisabled={isDisabled}
                    onChange={onChange}
                />
            );

        case 'address':
            return (
                <div className="tw-grid tw-min-w-[240px] tw-grid-cols-[1fr_64px] tw-gap-1">
                    {ADDRESS_PARTS.map((part) => (
                        <TextCell
                            key={part}
                            value={value?.[part] ?? ''}
                            placeholder={t(`booking.documentImport.address.${part}`)}
                            isInvalid={isInvalid}
                            isDisabled={isDisabled}
                            onChange={(next) => onChange({ ...(value ?? {}), [part]: next ?? '' })}
                        />
                    ))}
                </div>
            );

        case 'checkbox':
            if (options.length === 0) {
                return (
                    <StyledCheckbox
                        isSelected={Boolean(value)}
                        isDisabled={isDisabled}
                        onChange={(checked) => onChange(checked)}
                    />
                );
            }

            if ('single' !== (field.selectionMode ?? 'multiple')) {
                const selected: string[] = Array.isArray(value) ? value : [];

                return (
                    <div className="tw-flex tw-min-w-[160px] tw-flex-col tw-gap-1">
                        {options.map((option) => {
                            const label = optionLabel(option);

                            return (
                                <StyledCheckbox
                                    key={label}
                                    label={label}
                                    isSelected={selected.includes(label)}
                                    isDisabled={isDisabled}
                                    onChange={(checked) =>
                                        onChange(
                                            checked ? [...selected, label] : selected.filter((entry) => entry !== label)
                                        )
                                    }
                                />
                            );
                        })}
                    </div>
                );
            }

            return <ChoiceCell options={options} value={value} isDisabled={isDisabled} onChange={onChange} />;

        case 'table':
            return (
                <TableField
                    field={field}
                    value={value ?? field.rows?.map(() => field.columns?.map(() => false))}
                    isInvalid={isInvalid}
                    onChange={onChange}
                />
            );

        case 'select':
            return <ChoiceCell options={options} value={value} isDisabled={isDisabled} onChange={onChange} />;

        case 'payment_options':
            // stored is the price, shown is the option's name
            return (
                <ChoiceCell
                    options={options}
                    value={value}
                    optionValue={(option) => (typeof option === 'object' ? option.price : option)}
                    isDisabled={isDisabled}
                    onChange={onChange}
                />
            );

        default:
            return <TextCell value={value ?? ''} isInvalid={isInvalid} isDisabled={isDisabled} onChange={onChange} />;
    }
};

const ChoiceCell = ({
    options,
    value,
    optionValue = optionLabel,
    isDisabled,
    onChange
}: {
    options: NonNullable<ImportField['options']>;
    value: any;
    optionValue?: (option: NonNullable<ImportField['options']>[number]) => any;
    isDisabled?: boolean;
    onChange: (value: any) => void;
}) => {
    const { t } = useTranslation('bookings');
    const selected = options.find((option) => String(optionValue(option)) === String(value));

    return (
        <Dropdown
            label={selected ? optionLabel(selected) : t('booking.documentImport.empty')}
            flat
            color="light"
            block
            size="sm"
            disabled={isDisabled}
        >
            <Dropdown.Item label={t('booking.documentImport.empty')} onClick={() => onChange(null)} />
            {options.map((option) => (
                <Dropdown.Item
                    key={optionLabel(option)}
                    label={optionLabel(option)}
                    onClick={() => onChange(optionValue(option))}
                />
            ))}
        </Dropdown>
    );
};

export default CellEditor;
